import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import type {
  DropdownKey,
  DropdownList,
  DropdownQuery,
  DropdownSearchQuery,
  DropdownSearchResult,
  CompanySearchResult,
  PostalCodeSearchQuery,
  PostalCodeSearchResult,
} from "@/lib/types/dropdown.types";

/**
 * Dropdown / typeahead API.
 *
 * Talks to the Express middleware at `/api/dropdown/*`, which injects the
 * Dropdown Service's API key and forwards to it. The browser never holds that
 * key, so there is no direct call to the service from here.
 *
 * Routed through `axiosBaseQuery` like creditApi, so these share the session
 * handling in lib/axios — an expired access token is refreshed and retried
 * rather than leaving a filter panel permanently empty.
 */

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

/**
 * Returned instead of `undefined` when a request fails, so callers can render
 * `data.options` without guarding every access.
 */
const EMPTY_OPTIONS: DropdownOptionsResult = { options: [], source: "database", count: 0 };

const EMPTY_COMPANY_RESULT: CompanySearchResult = { query: "", count: 0, options: [] };

const EMPTY_POSTAL_RESULT: PostalCodeSearchResult = { query: "", count: 0, hasMore: false, options: [] };

interface DropdownOptionsResult {
  options: DropdownList["options"];
  source: DropdownList["source"];
  count: number;
}

/**
 * Serializes the group scope onto a URL.
 *
 * Written into the path rather than handed to axios as `params` because the
 * service takes a repeated parameter (`?group=US&group=GB`) and axios would
 * serialize an array as `group[]=US&group[]=GB` instead. A group code may
 * contain commas of its own — the industry sector "Oil, Gas, and Mining" — so a
 * delimited list is not an option either.
 */
function scopedUrl(
  path: string,
  scope: { groups?: string[]; groupPrefixes?: string[]; sectors?: string[] },
  extra: Record<string, string | number | undefined> = {}
): string {
  const search = new URLSearchParams();
  Object.entries(extra).forEach(([name, value]) => {
    if (value !== undefined && value !== "") search.set(name, String(value));
  });
  (scope.groups ?? []).forEach((group) => search.append("group", group));
  (scope.groupPrefixes ?? []).forEach((prefix) => search.append("groupPrefix", prefix));
  (scope.sectors ?? []).forEach((sector) => search.append("sector", sector));

  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Cache-key label for one scope. RTK Query keys a query by its arguments, so a
 * dropdown read is cached per (key, groups, prefixes) — which is what lets the
 * same states list back both the filter panel and a chip label lookup on one
 * request.
 */
function scopeTag({ groups, groupPrefixes, sectors }: DropdownQuery): string {
  const parts = [
    ...(groups ?? []),
    ...(groupPrefixes ?? []).map((p) => `${p}-*`),
    ...(sectors ?? []).map((s) => `sector:${s}`),
  ];
  return parts.length > 0 ? parts.join(",") : "";
}

export const dropdownApi = createApi({
  reducerPath: "dropdownApi",
  baseQuery: axiosBaseQuery("/api/dropdown"),
  tagTypes: ["Dropdown"],
  // Option lists change about never, and the service caches them for 5 minutes
  // anyway — refetching them because a window regained focus would be pure
  // noise. Held for an hour so a filter panel that is opened, closed and
  // reopened costs one request.
  refetchOnFocus: false,
  refetchOnReconnect: false,
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    /**
     * The option list for one dropdown, or for the groups of it a scope names.
     * Cached per (key, groups, prefixes), which is what lets the same list back
     * both a filter panel and the label lookup that renders selected chips
     * without issuing two requests.
     */
    getDropdown: builder.query<DropdownOptionsResult, DropdownQuery>({
      query: ({ key, groups, groupPrefixes, sectors }) =>
        scopedUrl(`/${key}`, { groups, groupPrefixes, sectors }),
      transformResponse: (response: Envelope<DropdownList | null>) =>
        response?.data
          ? {
              options: response.data.options ?? [],
              source: response.data.source,
              count: response.data.count ?? response.data.options?.length ?? 0,
            }
          : EMPTY_OPTIONS,
      providesTags: (_result, _error, args) => {
        const scope = scopeTag(args);
        return [{ type: "Dropdown" as const, id: scope ? `${args.key}:${scope}` : args.key }];
      },
    }),

    /**
     * Typeahead within one dropdown. Ranked upstream — label prefix, then value
     * prefix, then substring — so the order the service returns is the order to
     * render; re-sorting here would throw that ranking away.
     *
     * Debounce at the call site, not here: RTK Query caches per query string, so
     * every distinct keystroke is its own request until it is.
     */
    searchDropdown: builder.query<DropdownOptionsResult, DropdownSearchQuery>({
      query: ({ key, q, limit, groups, groupPrefixes, sectors }) =>
        scopedUrl(`/${key}/search`, { groups, groupPrefixes, sectors }, { q, limit }),
      transformResponse: (response: Envelope<DropdownSearchResult | null>) =>
        response?.data
          ? {
              options: response.data.options ?? [],
              source: response.data.source,
              count: response.data.count ?? response.data.options?.length ?? 0,
            }
          : EMPTY_OPTIONS,
      // Held far more briefly than a full list: a query is one person's
      // half-typed word, not shared reference data worth an hour of memory.
      keepUnusedDataFor: 120,
    }),

    /**
     * Company-name typeahead. Same service, but a different upstream
     * (Elasticsearch, not a MongoDB dropdown collection) — hence its own
     * endpoint rather than a DropdownKey, at `/companies/search`.
     */
    searchCompanies: builder.query<CompanySearchResult, { q: string }>({
      query: ({ q }) => `/companies/search?q=${encodeURIComponent(q)}`,
      transformResponse: (response: Envelope<CompanySearchResult | null>) =>
        response?.data ?? EMPTY_COMPANY_RESULT,
      keepUnusedDataFor: 120,
    }),

    /**
     * Postal-code typeahead. Same service, Elasticsearch-backed, at its own
     * `/postal-codes/search` - scoped by place names (`?city=London&city=Leeds`,
     * repeated like `group`), not by group codes. `q` is optional: with a scope
     * and no `q` the service lists that scope alphabetically.
     *
     * Scope values are sorted so picking London then Leeds hits the same cache
     * entry as Leeds then London.
     */
    searchPostalCodes: builder.query<PostalCodeSearchResult, PostalCodeSearchQuery>({
      query: ({ q, cities, states, countries, limit }) => {
        const search = new URLSearchParams();
        if (q) search.set("q", q);
        if (limit !== undefined) search.set("limit", String(limit));
        [...(cities ?? [])].sort().forEach((city) => search.append("city", city));
        [...(states ?? [])].sort().forEach((state) => search.append("state", state));
        [...(countries ?? [])].sort().forEach((country) => search.append("country", country));
        const qs = search.toString();
        return qs ? `/postal-codes/search?${qs}` : "/postal-codes/search";
      },
      transformResponse: (response: Envelope<PostalCodeSearchResult | null>) =>
        response?.data
          ? {
              query: response.data.query ?? "",
              count: response.data.count ?? response.data.options?.length ?? 0,
              hasMore: Boolean(response.data.hasMore),
              options: response.data.options ?? [],
            }
          : EMPTY_POSTAL_RESULT,
      keepUnusedDataFor: 120,
    }),
  }),
});

export const {
  useGetDropdownQuery,
  useSearchDropdownQuery,
  useSearchCompaniesQuery,
  useSearchPostalCodesQuery,
} = dropdownApi;

export type { DropdownKey, DropdownOptionsResult };
